'use client'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BofuFaqItem } from './types'

/**
 * Config-driven FAQ block for BOFU marketing pages. Mirrors
 * components/landing/faq.tsx's visual language exactly (rounded-2xl bordered
 * cards, animated grid-rows expand, rotating Plus icon) but takes
 * items/heading props instead of reading next-intl translations.
 */
export function BofuFaq({ items, heading }: { items: BofuFaqItem[]; heading: string }) {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section className="bg-white py-24" id="faq">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="font-serif text-4xl font-bold tracking-tight md:text-5xl">{heading}</h2>
        </div>

        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {items.map((item, i) => {
            const isOpen = open === i
            return (
              <div
                key={item.q}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-semibold"
                >
                  {item.q}
                  <Plus
                    className={cn(
                      'h-5 w-5 flex-shrink-0 text-primary transition-transform duration-200',
                      isOpen && 'rotate-45'
                    )}
                  />
                </button>
                <div
                  className={cn(
                    'grid transition-all duration-300',
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 leading-relaxed text-muted-foreground">{item.a}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
